import { tUi } from "../../../../../utils/dq_lc";
import { SLIDE_TYPE_LAYOUT_MAP } from "./preview.constants";

// Helper functions to handle legacy vs modern slide schemas (Gen 1, Gen 2, Gen 3)
export const getSlideId = (slide) => slide?.slide_id || slide?.SlideID || slide?._pbdq_origin?.slide_id || slide?._pbdq_origin?.SlideID;
export const getSlideOrder = (slide) => slide?.slide_order ?? slide?.SlideOrder;
export const getSlideGlossary = (slide) => slide?.slide_glossary || slide?.SlideGlossary || [];

export const getSlideLayout = (slide) => {
    if (!slide) return '';
    const origin = slide._pbdq_origin || slide;
    const type = (origin.slide_type || slide._pbdq_kind || '').toLowerCase();
    if (type === 'case_study' || slide._pbdq_kind === 'case_study' || origin.slide_type === 'case_study' || Boolean(origin.case_snippet || origin.case_title)) {
        return 'TemplateHtmlContent';
    }
    if (slide.SlideLayout) return slide.SlideLayout;
    if (slide.VideoUrl) return 'VideoTemplate';

    return SLIDE_TYPE_LAYOUT_MAP[type] || 'TemplateHtmlContent';
};

// Assessment title helpers
export const normalizeAssessmentTitle = (s) =>
    String(s || '')
        .replace(/[’‘]/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/[!?]+$/, '');

export const localizeAssessmentIntroTitle = (rawTitle, uiLocale) => {
    if (!rawTitle) return rawTitle;
    if (!uiLocale || uiLocale === 'en-US') return rawTitle;

    const n = normalizeAssessmentTitle(rawTitle);
    if (n === "let's take a quick test first") return tUi('preview.assessment.quickTestFirst', uiLocale);
    if (n === "let's test your knowledge and skills") return tUi('preview.assessment.testKnowledgeSkills', uiLocale);
    return rawTitle;
};

// API helpers for new format (language as key)
export const pickDefaultPreviewLanguage = (langs = []) => {
    const list = Array.isArray(langs) ? langs.filter(Boolean) : [];
    if (list.length <= 1) return list.includes('en-US') ? 'en-US' : (list[0] || 'en-US');
    return list.find(l => l !== 'en-US') || 'en-US';
};

export const getProgramPreviewAndAssessmentsFromResponse = (resData, preferredLang = 'en-US') => {
    const previews = resData?.program_layout_previews;
    if (!previews || typeof previews !== 'object') {
        return {
            language: 'en-US',
            languages: [],
            program: null,
            sessionAssessments: {},
        };
    }

    const langs = Object.keys(previews);
    const lang = langs.includes(preferredLang) ? preferredLang : pickDefaultPreviewLanguage(langs);
    const program = previews?.[lang]?.program_layout_preview || null;
    const sessAssess = (resData?.session_assessments_by_language && typeof resData.session_assessments_by_language === 'object')
        ? (resData.session_assessments_by_language?.[lang] || {})
        : {};

    return {
        language: lang,
        languages: langs,
        program,
        sessionAssessments: sessAssess,
    };
};

export const mapProgramToSidebar = (program) => {
    if (!program || !Array.isArray(program.modules)) return [];

    return program.modules.map(mod => ({
        module_code: mod.module_code || '',
        module_name: mod.module_title || '',
        module_description: mod.description_for_educator || '',
        module_duration: mod.total_duration_minutes || '',
        module_items_definitions: {
            title: mod.definition_title || '',
            description: mod.definition || ''
        },
        unique_dqco: mod.unique_dqco || [],
        units: (mod.units || []).map(unit => ({
            unit_code: unit.unit_code || '',
            unit_title: unit.unit_title || '',
            unit_description: unit.description_for_educator || '',
            unit_duration: unit.total_duration_minutes || 0,
            lessons: (unit.sessions || []).map(session => ({
                session_code: session.session_code || '',
                lesson_title: session.session_title || '',
                lesson_duration: session.duration_minutes || 0,
                lesson_description: session.description_for_educator || '',
                lesson_tokens: session.lesson_tokens || [],
                session_learning_objectives: session.session_learning_objectives || [],
            })),
        })),
    }));
};

export const getSloTitlesForSession = (session) => {
    // Handles both old and new data shape, expects array of SLOs
    if (!session?.session_learning_objectives) return {};
    const sloTitles = {};
    session.session_learning_objectives.forEach((slo, idx) => {
        sloTitles[`SLO${idx + 1}`] = slo.slo_title || `SLO${idx + 1}`;
    });
    return sloTitles;
};

export const getSegmentDisplayName = (segmentKey, session, uiLocale) => {
    if (segmentKey.startsWith("SLO")) {
        // Dynamically fetch SLO title from session object
        const sloTitles = getSloTitlesForSession(session);
        return sloTitles[segmentKey] || segmentKey;
    }

    const staticNames = {
        "Intro": tUi('preview.segment.introduction', uiLocale),
        "Seg1": tUi('preview.segment.introduction', uiLocale),
        "Seg4": tUi('preview.segment.reflection', uiLocale),
        "Closing": tUi('preview.segment.recap', uiLocale),
        "Seg5": tUi('preview.segment.recap', uiLocale),
        "Seg5_1": tUi('preview.segment.sessionScore', uiLocale)
    };
    return staticNames[segmentKey] || segmentKey;
};
